/**
 * Comprehensive Timeline Page
 * 
 * Shows ALL emails, meetings, journals, and tasks in a unified chronological view
 * Aggregates data from all sources and displays them in a single timeline
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LazyTimeline } from '@/components/LazyTimeline';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { 
  Mail, 
  Calendar, 
  Mic, 
  CheckSquare,
  Filter,
  Search,
  ArrowDownUp,
  RefreshCw,
  Loader2,
  FileText,
  Clock,
  User
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { blink } from '@/lib/blink';
import { timelineAggregationService } from '@/services/timelineAggregationService';

interface TimelineItem {
  id: string;
  type: 'email' | 'meeting' | 'journal' | 'task';
  date: string;
  title: string;
  description?: string;
  contact?: string;
  contactEmail?: string;
  participants?: string[];
  metadata?: {
    direction?: 'sent' | 'received';
    isUrgent?: boolean;
    isCompleted?: boolean;
    status?: string;
    priority?: string;
  };
}

const typeIcons = {
  email: Mail,
  meeting: Calendar,
  journal: Mic,
  task: CheckSquare,
};

const typeColors = {
  email: 'text-blue-500 bg-blue-500/10',
  meeting: 'text-green-500 bg-green-500/10',
  journal: 'text-purple-500 bg-purple-500/10',
  task: 'text-orange-500 bg-orange-500/10',
};

const typeLabels = {
  email: 'Email',
  meeting: 'Meeting',
  journal: 'Voice Journal',
  task: 'Task',
};

export default function TimelinePage() {
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadTimeline();
  }, []);

  const loadTimeline = async () => {
    setLoading(true);
    try {
      const user = await blink.auth.me();
      if (!user?.id) {
        console.error('No user found');
        setLoading(false);
        return;
      }

      const allItems: TimelineItem[] = [];

      // 1. Load all emails (from messages_enhanced)
      const messages = await blink.db.messagesEnhanced.list({
        where: { userId: user.id },
        orderBy: { sentDate: 'desc' },
        limit: 500,
      });

      for (const msg of messages) {
        allItems.push({
          id: `email_${msg.id}`,
          type: 'email',
          date: msg.sentDate || msg.receivedDate || msg.createdAt,
          title: msg.subject || 'No Subject',
          description: msg.snippet || msg.bodyText?.substring(0, 200),
          contact: msg.senderName || msg.senderEmail || 'Unknown',
          contactEmail: msg.senderEmail,
          participants: parseRecipients(msg.toRecipients),
          metadata: {
            direction: msg.toRecipients ? 'sent' : 'received',
            isUrgent: Number(msg.isImportant) > 0,
          },
        });
      }

      // 2. Load all meetings (from calendar_events_enhanced)
      const events = await blink.db.calendarEventsEnhanced.list({
        where: { userId: user.id },
        orderBy: { startTime: 'desc' },
        limit: 500,
      });

      for (const event of events) {
        allItems.push({
          id: `meeting_${event.id}`,
          type: 'meeting',
          date: event.startTime,
          title: event.title || 'Meeting',
          description: event.aiSummary || event.description,
          participants: parseAttendees(event.attendees),
          metadata: {
            status: event.status,
          },
        });
      }

      // 3. Load all voice journals
      const journals = await blink.db.voiceJournals.list({
        where: { userId: user.id },
        orderBy: { recordedAt: 'desc' },
        limit: 500,
      });

      for (const journal of journals) {
        allItems.push({
          id: `journal_${journal.id}`,
          type: 'journal',
          date: journal.recordedAt,
          title: journal.title || 'Voice Journal',
          description: journal.content?.substring(0, 200),
          contact: journal.contactEmail,
          contactEmail: journal.contactEmail,
          metadata: {},
        });
      }

      // 4. Load all tasks
      const tasks = await blink.db.tasks.list({
        where: { userId: user.id },
        orderBy: { createdAt: 'desc' },
        limit: 500,
      });

      for (const task of tasks) {
        allItems.push({
          id: `task_${task.id}`,
          type: 'task',
          date: task.dueDate || task.createdAt,
          title: task.title,
          description: task.description,
          metadata: {
            isCompleted: task.status === 'completed',
            priority: task.priority,
            status: task.status,
          },
        });
      }

      setTimeline(allItems);
    } catch (error) {
      console.error('Failed to load timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncTimeline = async () => {
    setSyncing(true);
    try {
      // Force background aggregation service to run
      await timelineAggregationService.forceSyncNow();
      // Then reload the timeline
      await loadTimeline();
    } finally {
      setSyncing(false);
    }
  };

  const parseRecipients = (recipientsData: any): string[] => {
    if (!recipientsData) return [];
    
    try {
      if (typeof recipientsData === 'string') {
        const parsed = JSON.parse(recipientsData);
        if (Array.isArray(parsed)) {
          return parsed.map(r => {
            if (typeof r === 'string') return r;
            if (typeof r === 'object' && r.email) return r.email;
            return '';
          }).filter(Boolean);
        }
      }
      if (Array.isArray(recipientsData)) {
        return recipientsData.map(r => {
          if (typeof r === 'string') return r;
          if (typeof r === 'object' && r.email) return r.email;
          return '';
        }).filter(Boolean);
      }
      return [];
    } catch {
      return [];
    }
  };

  const parseAttendees = (attendeesData: any): string[] => {
    if (!attendeesData) return [];
    
    try {
      if (typeof attendeesData === 'string') {
        const parsed = JSON.parse(attendeesData);
        if (Array.isArray(parsed)) {
          return parsed.map(a => {
            if (typeof a === 'string') return a;
            if (typeof a === 'object' && a.email) return a.email;
            return '';
          }).filter(Boolean);
        }
        return attendeesData.split(',').map(a => a.trim()).filter(Boolean);
      }
      if (Array.isArray(attendeesData)) {
        return attendeesData.map(a => {
          if (typeof a === 'string') return a;
          if (typeof a === 'object' && a.email) return a.email;
          return '';
        }).filter(Boolean);
      }
      return [];
    } catch {
      return [];
    }
  };

  // Filter and sort timeline
  const filteredTimeline = timeline.filter(item => {
    // Apply type filter
    if (filterType && item.type !== filterType) return false;
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = item.title.toLowerCase().includes(query);
      const matchesDescription = item.description?.toLowerCase().includes(query);
      const matchesContact = item.contact?.toLowerCase().includes(query);
      const matchesEmail = item.contactEmail?.toLowerCase().includes(query);
      
      return matchesTitle || matchesDescription || matchesContact || matchesEmail;
    }
    
    return true;
  });

  const sortedTimeline = [...filteredTimeline].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Calculate stats
  const stats = {
    total: timeline.length,
    emails: timeline.filter(t => t.type === 'email').length,
    meetings: timeline.filter(t => t.type === 'meeting').length,
    journals: timeline.filter(t => t.type === 'journal').length,
    tasks: timeline.filter(t => t.type === 'task').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading timeline...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold gradient-text mb-2">Complete Timeline</h1>
        <p className="text-muted-foreground">
          All your emails, meetings, journals, and tasks in one chronological view
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-muted-foreground opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Emails</p>
                <p className="text-2xl font-bold text-blue-500">{stats.emails}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Meetings</p>
                <p className="text-2xl font-bold text-green-500">{stats.meetings}</p>
              </div>
              <Calendar className="w-8 h-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Journals</p>
                <p className="text-2xl font-bold text-purple-500">{stats.journals}</p>
              </div>
              <Mic className="w-8 h-8 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasks</p>
                <p className="text-2xl font-bold text-orange-500">{stats.tasks}</p>
              </div>
              <CheckSquare className="w-8 h-8 text-orange-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <CardTitle>Timeline</CardTitle>
            
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search timeline..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              >
                <ArrowDownUp className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                onClick={syncTimeline}
                disabled={syncing}
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Type filters */}
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <Button
              variant={filterType === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType(null)}
            >
              All ({stats.total})
            </Button>
            <Button
              variant={filterType === 'email' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('email')}
            >
              <Mail className="w-3 h-3 mr-1" />
              Emails ({stats.emails})
            </Button>
            <Button
              variant={filterType === 'meeting' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('meeting')}
            >
              <Calendar className="w-3 h-3 mr-1" />
              Meetings ({stats.meetings})
            </Button>
            <Button
              variant={filterType === 'journal' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('journal')}
            >
              <Mic className="w-3 h-3 mr-1" />
              Journals ({stats.journals})
            </Button>
            <Button
              variant={filterType === 'task' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterType('task')}
            >
              <CheckSquare className="w-3 h-3 mr-1" />
              Tasks ({stats.tasks})
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {sortedTimeline.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg mb-2">No items found</p>
              <p className="text-sm">
                {searchQuery ? 'Try adjusting your search query' : 'Your timeline will appear here'}
              </p>
            </div>
          ) : (
            <LazyTimeline 
              items={sortedTimeline}
              className="h-[calc(100vh-450px)]"
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
